// src/components/issues/IssueTable.js
import React, { useState, useMemo, useCallback, memo } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, IconButton, HStack, Text, Badge,
  Menu, MenuButton, MenuList, MenuItem, useColorModeValue, Link, Box, Tooltip, Icon,
  Checkbox, Button, Skeleton, Flex, Tag, VStack, MenuDivider, useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Textarea, FormControl, FormLabel, Alert, AlertIcon, Input, InputGroup, InputLeftElement,
  Select, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, Drawer, DrawerOverlay,
  DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton, Divider, Code, Stack,
  Switch, useToast, Portal, AlertTitle, AlertDescription
} from '@chakra-ui/react';
import { 
  MdEdit, MdDelete, MdCheckCircle, MdMoreVert, MdLocationOn, 
  MdArrowUpward, MdArrowDownward, MdRemoveRedEye, MdContentCopy,
  MdTimeline, MdWarning, MdSearch, MdFilterList, MdClose, MdDownload,
  MdPrint, MdShare, MdAttachment, MdHistory, MdOpenInNew
} from 'react-icons/md';
import { formatDistanceToNowStrict, format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// ============================================================================
// OPTIMIZED ANIMATION VARIANTS
// ============================================================================
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.02,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.2 }
  }
};

const DEBUG_CONTEXT = '[IssueTable]';

const logDebug = (message, data = null) => {
  const timestamp = new Date().toISOString();
  console.log(`${timestamp} ${DEBUG_CONTEXT} ${message}`, data || '');
};

const getStatusColorScheme = (status) => {
  const s = status?.toLowerCase();
  if (s === 'open') return 'red';
  if (s === 'pending_assignment') return 'orange';
  if (s === 'in_progress') return 'blue';
  if (s === 'on_hold') return 'yellow';
  if (s === 'resolved') return 'green';
  if (s === 'closed') return 'gray';
  return 'gray';
};

const getSeverityColorScheme = (severity) => {
  const s = severity?.toLowerCase();
  if (s === 'critical') return 'red';
  if (s === 'high') return 'orange';
  if (s === 'medium') return 'yellow';
  if (s === 'low') return 'green';
  return 'gray';
};

const getIssueAge = (reportedAt) => {
  const hours = (Date.now() - new Date(reportedAt).getTime()) / (1000 * 60 * 60);
  if (hours < 24) return { label: 'Recent', color: 'green' };
  if (hours < 72) return { label: 'Active', color: 'blue' };
  if (hours < 168) return { label: 'Week Old', color: 'orange' };
  return { label: 'Stale', color: 'red' };
};

// ============================================================================
// MEMOIZED TABLE ROW COMPONENT FOR PERFORMANCE
// ============================================================================
const TableRow = memo(({ 
  issue, 
  index,
  isSelected, 
  isAdmin,
  onSelectIssue,
  onEditIssue,
  onResolveIssue,
  onDeleteIssue,
  onOpenResolveModal,
  borderColor,
  textColor,
  hoverBg,
  selectedBg
}) => {
  const ageInfo = getIssueAge(issue.reported_at);
  
  const handleCopyCoordinates = useCallback(() => {
    const coords = `${issue.latitude}, ${issue.longitude}`;
    navigator.clipboard.writeText(coords);
    logDebug('Coordinates copied', { coords });
  }, [issue.latitude, issue.longitude]);

  return (
    <Tr 
      _hover={{ bg: hoverBg }}
      bg={isSelected ? selectedBg : "transparent"}
      transition="all 0.15s"
    >
      {isAdmin && (
        <Td borderColor={borderColor}>
          <Checkbox
            isChecked={isSelected}
            onChange={() => onSelectIssue(issue.id)}
            colorScheme="blue"
          />
        </Td>
      )}
      <Td borderColor={borderColor} fontWeight="bold">
        <Tooltip label={`Issue #${issue.id}`}>
          <Text color="blue.600" fontWeight="800" fontSize="sm">#{issue.id}</Text>
        </Tooltip>
      </Td>
      <Td borderColor={borderColor}>
        <Badge 
          colorScheme={issue.issue_type ? 'purple' : 'gray'} 
          variant="subtle" 
          px={3} 
          py={1} 
          borderRadius="lg"
          fontSize="2xs"
          fontWeight="700"
        >
          {issue.issue_type?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
        </Badge>
      </Td>
      <Td borderColor={borderColor} maxW="300px">
        <Tooltip label={issue.description} placement="top-start" hasArrow>
          <VStack align="start" spacing={1}>
            <Text isTruncated noOfLines={2} fontSize="sm" fontWeight="500">
              {issue.description || 'No description'}
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              {issue.linked_reports_count > 0 && (
                <Badge colorScheme="blue" fontSize="2xs" borderRadius="full">
                  +{issue.linked_reports_count} linked
                </Badge>
              )}
              {/* 🚀 ENTERPRISE UPDATE: Helpdesk Origin Badge */}
              {issue.helpdesk_ticket_id && (
                <Badge 
                  as="a" 
                  href={`/admin/helpdesk?ticket=${issue.helpdesk_ticket_number}`}
                  colorScheme="purple" 
                  fontSize="2xs" 
                  borderRadius="full"
                  cursor="pointer"
                  _hover={{ bg: 'purple.500', color: 'white', textDecoration: 'none' }}
                  transition="all 0.2s"
                  display="flex"
                  alignItems="center"
                >
                  <Icon as={MdOpenInNew} mr={1} />
                  Origin: Helpdesk #{issue.helpdesk_ticket_number}
                </Badge>
              )}
            </HStack>
          </VStack>
        </Tooltip>
      </Td>
      <Td borderColor={borderColor}>
        <Badge 
          colorScheme={getStatusColorScheme(issue.status)} 
          variant="solid" 
          px={3} 
          py={1.5}
          borderRadius="lg"
          fontSize="2xs"
          fontWeight="700"
        >
          {issue.status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
        </Badge>
      </Td>
      <Td borderColor={borderColor}>
        <Tooltip label={issue.severity || 'Not specified'}>
          <Badge 
            colorScheme={getSeverityColorScheme(issue.severity)} 
            variant="outline"
            px={3} 
            py={1}
            borderRadius="lg"
            fontSize="2xs"
            fontWeight="700"
            borderWidth="2px"
          >
            {issue.severity?.toUpperCase() || 'N/A'}
          </Badge>
        </Tooltip>
      </Td>
      <Td borderColor={borderColor}>
        <VStack align="start" spacing={1}>
          <Tooltip label={format(new Date(issue.reported_at), "PPpp")} placement="top" hasArrow>
            <Text fontSize="xs" fontWeight="600">
              {formatDistanceToNowStrict(new Date(issue.reported_at), { addSuffix: true })}
            </Text>
          </Tooltip>
          <Tag size="sm" colorScheme={ageInfo.color} fontSize="2xs" borderRadius="full">
            {ageInfo.label}
          </Tag>
        </VStack>
      </Td>
      <Td borderColor={borderColor}>
        <VStack align="start" spacing={1}>
          <Link 
            href={`https://maps.google.com/?q=${issue.latitude},${issue.longitude}`}
            isExternal 
            color="blue.500"
            _hover={{ textDecoration: 'underline', color: 'blue.600' }}
            fontSize="xs"
            fontWeight="600"
          >
            <HStack spacing={1}>
              <Icon as={MdLocationOn} boxSize={4} />
              <Text>
                {parseFloat(issue.latitude).toFixed(4)}, {parseFloat(issue.longitude).toFixed(4)}
              </Text>
            </HStack>
          </Link>
          <IconButton
            icon={<Icon as={MdContentCopy} />}
            size="2xs"
            variant="ghost"
            onClick={handleCopyCoordinates}
            aria-label="Copy coordinates"
            colorScheme="blue"
          />
        </VStack>
      </Td>
      {isAdmin && (
        <Td borderColor={borderColor} textAlign="right">
          <Menu>
            <MenuButton 
              as={IconButton} 
              aria-label="Actions"
              icon={<MdMoreVert />} 
              variant="ghost" 
              size="sm"
              borderRadius="lg"
            />
            <MenuList fontSize="sm" borderRadius="xl" boxShadow="xl">
              <MenuItem 
                icon={<Icon as={MdRemoveRedEye} boxSize="16px"/>} 
                onClick={() => onEditIssue(issue)}
                borderRadius="md"
              >
                View Details
              </MenuItem>
              <MenuItem 
                icon={<Icon as={MdEdit} boxSize="16px"/>} 
                onClick={() => onEditIssue(issue)}
                borderRadius="md"
              >
                Edit Issue
              </MenuItem>
              {issue.linked_reports_count > 0 && (
                <MenuItem 
                  icon={<Icon as={MdTimeline} boxSize="16px"/>}
                  isDisabled
                  borderRadius="md"
                >
                  View Timeline ({issue.linked_reports_count})
                </MenuItem>
              )}
              <MenuDivider />
              {(issue.status !== 'resolved' && issue.status !== 'closed') && (
                <MenuItem 
                  icon={<Icon as={MdCheckCircle} boxSize="16px"/>}
                  onClick={() => onOpenResolveModal(issue)}
                  color="green.500"
                  borderRadius="md"
                >
                  Mark as Resolved
                </MenuItem>
              )}
              <MenuItem 
                icon={<Icon as={MdDelete} boxSize="16px"/>} 
                onClick={() => onDeleteIssue(issue.id)} 
                color="red.500"
                borderRadius="md"
              >
                Delete Issue
              </MenuItem>
            </MenuList>
          </Menu>
        </Td>
      )}
    </Tr>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for performance optimization
  return (
    prevProps.issue.id === nextProps.issue.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.issue.status === nextProps.issue.status &&
    prevProps.issue.severity === nextProps.issue.severity
  );
});

TableRow.displayName = 'TableRow';

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const IssueTable = ({ 
  issues, 
  onEditIssue, 
  onResolveIssue, 
  onDeleteIssue, 
  isLoading, 
  isAdmin,
  onSortChange,
  sortConfig
}) => {
  const [selectedIssues, setSelectedIssues] = useState(new Set());
  const [resolvingIssue, setResolvingIssue] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [quickSearch, setQuickSearch] = useState('');
  const [columnFilter, setColumnFilter] = useState({});
  const [viewingIssue, setViewingIssue] = useState(null);
  
  const { isOpen: isResolveModalOpen, onOpen: onResolveModalOpen, onClose: onResolveModalClose } = useDisclosure();
  const { isOpen: isDetailsDrawerOpen, onOpen: onDetailsDrawerOpen, onClose: onDetailsDrawerClose } = useDisclosure();
  const { isOpen: isFilterOpen, onToggle: onFilterToggle } = useDisclosure({ defaultIsOpen: false });
  
  const toast = useToast();

  // Theme colors - ALL hooks must be at the top level
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const headerBg = useColorModeValue("gray.100", "navy.800");
  const bulkActionsBg = useColorModeValue('blue.50', 'blue.900');
  const headerHoverBg = useColorModeValue('gray.200', 'navy.700');
  const modalBg = useColorModeValue("white", "navy.800");
  const footerBg = useColorModeValue("gray.50", "navy.900");
  const modalDetailsBg = useColorModeValue('gray.50', 'navy.700');
  const filterBg = useColorModeValue('white', 'navy.700');
  const drawerBg = useColorModeValue('white', 'navy.800');
  const rowHoverBg = useColorModeValue("#F7FAFC", "#111C44"); 
  const rowSelectedBg = useColorModeValue("#EBF8FF", "#1B254B"); 

  // ============================================================================
  // FILTERED & SORTED ISSUES (MOVED UP)
  // Must be defined before handlers that depend on it
  // ============================================================================
  const filteredIssues = useMemo(() => {
    let filtered = [...(issues || [])];

    // Quick search filter
    if (quickSearch) {
      const search = quickSearch.toLowerCase();
      filtered = filtered.filter(issue => 
        issue.id?.toString().includes(search) ||
        issue.description?.toLowerCase().includes(search) ||
        issue.issue_type?.toLowerCase().includes(search) ||
        issue.status?.toLowerCase().includes(search)
      );
    }

    // Column filters
    Object.entries(columnFilter).forEach(([key, value]) => {
      if (value) {
        filtered = filtered.filter(issue => issue[key] === value);
      }
    });

    return filtered;
  }, [issues, quickSearch, columnFilter]);

  // ============================================================================
  // TABLE STATISTICS (MOVED UP)
  // Must be defined before rendering
  // ============================================================================
  const tableStats = useMemo(() => {
    if (!filteredIssues || filteredIssues.length === 0) return null;
    
    return {
      total: filteredIssues.length,
      selected: selectedIssues.size,
      critical: filteredIssues.filter(i => i.severity === 'critical').length,
      high: filteredIssues.filter(i => i.severity === 'high').length,
      open: filteredIssues.filter(i => i.status === 'open').length,
      resolved: filteredIssues.filter(i => i.status === 'resolved' || i.status === 'closed').length,
      in_progress: filteredIssues.filter(i => i.status === 'in_progress').length,
    };
  }, [filteredIssues, selectedIssues]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleSort = useCallback((field) => {
    if (!onSortChange) return;
    
    const newDirection = sortConfig?.sortBy === field && sortConfig?.sortDirection === 'ASC' 
      ? 'DESC' 
      : 'ASC';
    
    logDebug('Sort changed', { field, direction: newDirection });
    onSortChange(field, newDirection);
  }, [sortConfig, onSortChange]);

  const getSortIcon = useCallback((field) => {
    if (sortConfig?.sortBy !== field) return null;
    return sortConfig?.sortDirection === 'ASC' 
      ? <Icon as={MdArrowUpward} boxSize={3} /> 
      : <Icon as={MdArrowDownward} boxSize={3} />;
  }, [sortConfig]);

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      const newSelected = new Set(filteredIssues.map(issue => issue.id));
      logDebug('Selected all issues', { count: newSelected.size });
      setSelectedIssues(newSelected);
    } else {
      logDebug('Deselected all issues');
      setSelectedIssues(new Set());
    }
  }, [filteredIssues]);

  const handleSelectIssue = useCallback((issueId) => {
    setSelectedIssues(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(issueId)) {
        newSelected.delete(issueId);
        logDebug('Deselected issue', { issueId });
      } else {
        newSelected.add(issueId);
        logDebug('Selected issue', { issueId });
      }
      return newSelected;
    });
  }, []);

  const handleBulkResolve = useCallback(() => {
    logDebug('Bulk resolve initiated', { count: selectedIssues.size });
    
    if (window.confirm(`Are you sure you want to resolve ${selectedIssues.size} selected issue(s)?`)) {
      const notes = window.prompt("Enter resolution notes (optional):");
      if (notes !== null) {
        selectedIssues.forEach(issueId => {
          onResolveIssue(issueId, { resolution_notes: notes });
        });
        setSelectedIssues(new Set());
        toast({
          title: 'Issues Resolved',
          description: `Successfully resolved ${selectedIssues.size} issue(s).`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  }, [selectedIssues, onResolveIssue, toast]);

  const handleBulkDelete = useCallback(() => {
    logDebug('Bulk delete initiated', { count: selectedIssues.size });
    
    if (window.confirm(`Are you sure you want to delete ${selectedIssues.size} selected issue(s)? This action cannot be undone.`)) {
      selectedIssues.forEach(issueId => {
        onDeleteIssue(issueId);
      });
      setSelectedIssues(new Set());
      toast({
        title: 'Issues Deleted',
        description: `Successfully deleted ${selectedIssues.size} issue(s).`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [selectedIssues, onDeleteIssue, toast]);

  const handleOpenResolveModal = useCallback((issue) => {
    logDebug('Opening resolve modal', { issueId: issue.id });
    setResolvingIssue(issue);
    setResolutionNotes('');
    onResolveModalOpen();
  }, [onResolveModalOpen]);

  const handleSubmitResolution = useCallback(() => {
    if (resolvingIssue) {
      logDebug('Submitting resolution', { 
        issueId: resolvingIssue.id, 
        hasNotes: !!resolutionNotes 
      });
      
      onResolveIssue(resolvingIssue.id, { resolution_notes: resolutionNotes });
      onResolveModalClose();
      setResolvingIssue(null);
      setResolutionNotes('');
      
      toast({
        title: 'Issue Resolved',
        description: `Issue #${resolvingIssue.id} has been marked as resolved.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [resolvingIssue, resolutionNotes, onResolveIssue, onResolveModalClose, toast]);

  const handleViewDetails = useCallback((issue) => {
    setViewingIssue(issue);
    onDetailsDrawerOpen();
  }, [onDetailsDrawerOpen]);

  const handleExportSelected = useCallback(() => {
    const selectedData = issues.filter(i => selectedIssues.has(i.id));
    const csvContent = [
      ['ID', 'Type', 'Description', 'Status', 'Severity', 'Reported At'].join(','),
      ...selectedData.map(issue => [
        issue.id,
        issue.issue_type,
        `"${(issue.description || '').replace(/"/g, '""')}"`,
        issue.status,
        issue.severity,
        issue.reported_at
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `issues_export_${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Export Successful',
      description: `Exported ${selectedData.length} issue(s).`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  }, [issues, selectedIssues, toast]);


  // ============================================================================
  // LOADING STATE
  // ============================================================================
  if (isLoading) {
    return (
      <VStack spacing={2} align="stretch">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height="50px" borderRadius="lg" />
        ))}
      </VStack>
    );
  }

  // ============================================================================
  // EMPTY STATE
  // ============================================================================
  if (!issues || issues.length === 0) {
    return (
      <MotionBox
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Box textAlign="center" py={12}>
          <Icon as={MdWarning} boxSize={16} color="gray.400" mb={4} />
          <Text color={textColor} fontSize="lg" fontWeight="bold">No Issues Found</Text>
          <Text color="gray.500" fontSize="sm" mt={2}>No issues have been reported yet.</Text>
        </Box>
      </MotionBox>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <>
      <VStack spacing={4} align="stretch">
        {/* Enhanced Quick Search & Actions Bar */}
        <MotionBox
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Flex 
            gap={3} 
            flexWrap="wrap" 
            align="center"
            p={4}
            bg={filterBg}
            borderRadius="xl"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
          >
            <InputGroup flex="1" minW="250px">
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Quick search (ID, description, type, status)..."
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                borderRadius="lg"
                size="sm"
              />
            </InputGroup>

            <HStack>
              <Button
                size="sm"
                variant={isFilterOpen ? "solid" : "ghost"}
                colorScheme={isFilterOpen ? "blue" : "gray"}
                leftIcon={<Icon as={MdFilterList} />}
                onClick={onFilterToggle}
                borderRadius="lg"
              >
                Filters
              </Button>

              {selectedIssues.size > 0 && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Icon as={MdDownload} />}
                    onClick={handleExportSelected}
                    borderRadius="lg"
                  >
                    Export ({selectedIssues.size})
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Icon as={MdClose} />}
                    onClick={() => setSelectedIssues(new Set())}
                    borderRadius="lg"
                  >
                    Clear
                  </Button>
                </>
              )}
            </HStack>
          </Flex>
        </MotionBox>

        {/* Column Filters */}
        <AnimatePresence>
          {isFilterOpen && (
            <MotionBox
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Box
                p={4}
                bg={filterBg}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                boxShadow="sm"
              >
                <Text fontSize="sm" fontWeight="bold" mb={3}>Advanced Filters</Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <Select
                    size="sm"
                    placeholder="Filter by Status"
                    value={columnFilter.status || ''}
                    onChange={(e) => setColumnFilter(prev => ({ ...prev, status: e.target.value }))}
                    borderRadius="lg"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </Select>

                  <Select
                    size="sm"
                    placeholder="Filter by Severity"
                    value={columnFilter.severity || ''}
                    onChange={(e) => setColumnFilter(prev => ({ ...prev, severity: e.target.value }))}
                    borderRadius="lg"
                  >
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </Select>

                  <Select
                    size="sm"
                    placeholder="Filter by Type"
                    value={columnFilter.issue_type || ''}
                    onChange={(e) => setColumnFilter(prev => ({ ...prev, issue_type: e.target.value }))}
                    borderRadius="lg"
                  >
                    <option value="leak">Leak</option>
                    <option value="blockage">Blockage</option>
                    <option value="damage">Damage</option>
                    <option value="malfunction">Malfunction</option>
                  </Select>
                </SimpleGrid>
                <Button
                  size="xs"
                  variant="ghost"
                  mt={3}
                  onClick={() => setColumnFilter({})}
                  leftIcon={<Icon as={MdClose} />}
                >
                  Clear Filters
                </Button>
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>

        {/* Bulk Actions Bar */}
        <AnimatePresence>
          {isAdmin && selectedIssues.size > 0 && (
            <MotionBox
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <Flex 
                p={4} 
                bg={bulkActionsBg} 
                borderRadius="xl" 
                justify="space-between"
                align="center"
                boxShadow="md"
                borderWidth="1px"
                borderColor="blue.200"
              >
                <HStack>
                  <Icon as={MdCheckCircle} color="blue.600" boxSize={5} />
                  <Text fontWeight="bold" color={textColor}>
                    {selectedIssues.size} issue(s) selected
                  </Text>
                </HStack>
                <HStack>
                  <Button 
                    size="sm" 
                    colorScheme="green" 
                    leftIcon={<Icon as={MdCheckCircle} />}
                    onClick={handleBulkResolve}
                    borderRadius="lg"
                    boxShadow="sm"
                  >
                    Resolve
                  </Button>
                  <Button 
                    size="sm" 
                    colorScheme="red" 
                    leftIcon={<Icon as={MdDelete} />}
                    onClick={handleBulkDelete}
                    borderRadius="lg"
                    boxShadow="sm"
                  >
                    Delete
                  </Button>
                </HStack>
              </Flex>
            </MotionBox>
          )}
        </AnimatePresence>

        {/* Enhanced Statistics */}
        {tableStats && (
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={3}>
              <Stat
                p={3}
                bg={filterBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                size="sm"
              >
                <StatLabel fontSize="xs">Total</StatLabel>
                <StatNumber fontSize="xl">{tableStats.total}</StatNumber>
              </Stat>
              
              <Stat
                p={3}
                bg={filterBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                size="sm"
              >
                <StatLabel fontSize="xs">Open</StatLabel>
                <StatNumber fontSize="xl" color="red.500">{tableStats.open}</StatNumber>
              </Stat>

              <Stat
                p={3}
                bg={filterBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                size="sm"
              >
                <StatLabel fontSize="xs">In Progress</StatLabel>
                <StatNumber fontSize="xl" color="blue.500">{tableStats.in_progress}</StatNumber>
              </Stat>

              <Stat
                p={3}
                bg={filterBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                size="sm"
              >
                <StatLabel fontSize="xs">Resolved</StatLabel>
                <StatNumber fontSize="xl" color="green.500">{tableStats.resolved}</StatNumber>
              </Stat>

              <Stat
                p={3}
                bg={filterBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                size="sm"
              >
                <StatLabel fontSize="xs">Critical</StatLabel>
                <StatNumber fontSize="xl" color="red.600">{tableStats.critical}</StatNumber>
              </Stat>

              <Stat
                p={3}
                bg={filterBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                size="sm"
              >
                <StatLabel fontSize="xs">High</StatLabel>
                <StatNumber fontSize="xl" color="orange.500">{tableStats.high}</StatNumber>
              </Stat>
            </SimpleGrid>
          </MotionBox>
        )}

        {/* Table */}
        <MotionBox
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box 
            overflowX="auto" 
            borderWidth="1px" 
            borderColor={borderColor} 
            borderRadius="xl"
            boxShadow="lg"
          >
            <Table variant="simple" size="sm" color={textColor}>
              <Thead bg={headerBg}>
                <Tr>
                  {isAdmin && (
                    <Th borderColor={borderColor} w="40px">
                      <Checkbox
                        isChecked={selectedIssues.size === filteredIssues.length && filteredIssues.length > 0}
                        isIndeterminate={selectedIssues.size > 0 && selectedIssues.size < filteredIssues.length}
                        onChange={handleSelectAll}
                        colorScheme="blue"
                      />
                    </Th>
                  )}
                  <Th 
                    borderColor={borderColor} 
                    cursor="pointer" 
                    onClick={() => handleSort('id')}
                    _hover={{ bg: headerHoverBg }}
                    borderRadius="md"
                    transition="all 0.2s"
                  >
                    <HStack spacing={1}>
                      <Text fontWeight="700">ID</Text>
                      {getSortIcon('id')}
                    </HStack>
                  </Th>
                  <Th 
                    borderColor={borderColor}
                    cursor="pointer"
                    onClick={() => handleSort('issue_type')}
                    _hover={{ bg: headerHoverBg }}
                    borderRadius="md"
                    transition="all 0.2s"
                  >
                    <HStack spacing={1}>
                      <Text fontWeight="700">Type</Text>
                      {getSortIcon('issue_type')}
                    </HStack>
                  </Th>
                  <Th borderColor={borderColor} fontWeight="700">Description</Th>
                  <Th 
                    borderColor={borderColor}
                    cursor="pointer"
                    onClick={() => handleSort('status')}
                    _hover={{ bg: headerHoverBg }}
                    borderRadius="md"
                    transition="all 0.2s"
                  >
                    <HStack spacing={1}>
                      <Text fontWeight="700">Status</Text>
                      {getSortIcon('status')}
                    </HStack>
                  </Th>
                  <Th 
                    borderColor={borderColor}
                    cursor="pointer"
                    onClick={() => handleSort('severity')}
                    _hover={{ bg: headerHoverBg }}
                    borderRadius="md"
                    transition="all 0.2s"
                  >
                    <HStack spacing={1}>
                      <Text fontWeight="700">Severity</Text>
                      {getSortIcon('severity')}
                    </HStack>
                  </Th>
                  <Th 
                    borderColor={borderColor}
                    cursor="pointer"
                    onClick={() => handleSort('reported_at')}
                    _hover={{ bg: headerHoverBg }}
                    borderRadius="md"
                    transition="all 0.2s"
                  >
                    <HStack spacing={1}>
                      <Text fontWeight="700">Reported</Text>
                      {getSortIcon('reported_at')}
                    </HStack>
                  </Th>
                  <Th borderColor={borderColor} fontWeight="700">Location</Th>
                  {isAdmin && <Th borderColor={borderColor} textAlign="right" fontWeight="700">Actions</Th>}
                </Tr>
              </Thead>
              <Tbody>
                <AnimatePresence>
                  {filteredIssues.map((issue, index) => {
                    const isSelected = selectedIssues.has(issue.id);
                    
                    return (
                      <TableRow
                        key={issue.id}
                        issue={issue}
                        index={index}
                        isSelected={isSelected}
                        isAdmin={isAdmin}
                        onSelectIssue={handleSelectIssue}
                        onEditIssue={onEditIssue}
                        onResolveIssue={onResolveIssue}
                        onDeleteIssue={onDeleteIssue}
                        onOpenResolveModal={handleOpenResolveModal}
                        borderColor={borderColor}
                        textColor={textColor}
                        hoverBg={rowHoverBg}
                        selectedBg={rowSelectedBg}
                      />
                    );
                  })}
                </AnimatePresence>
              </Tbody>
            </Table>
          </Box>
        </MotionBox>

        {/* No Results Message */}
        {filteredIssues.length === 0 && (quickSearch || Object.keys(columnFilter).length > 0) && (
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Alert status="info" borderRadius="xl">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">No matching issues found</AlertTitle>
                <AlertDescription fontSize="xs">
                  Try adjusting your search or filter criteria.
                </AlertDescription>
              </Box>
              <Button
                ml="auto"
                size="sm"
                onClick={() => {
                  setQuickSearch('');
                  setColumnFilter({});
                }}
              >
                Clear All
              </Button>
            </Alert>
          </MotionBox>
        )}
      </VStack>

      {/* Resolve Issue Modal */}
      <Modal isOpen={isResolveModalOpen} onClose={onResolveModalClose} size="md">
        <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.600" />
        <ModalContent bg={modalBg} borderRadius="2xl">
          <ModalHeader borderBottomWidth="1px" fontWeight="800">
            Resolve Issue #{resolvingIssue?.id}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={5} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={2}>Issue Details:</Text>
                <Text fontSize="xs" color="gray.600" p={3} bg={modalDetailsBg} borderRadius="lg">
                  {resolvingIssue?.description}
                </Text>
              </Box>
              
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">Resolution Notes (Optional)</FormLabel>
                <Textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Describe how this issue was resolved..."
                  rows={4}
                  fontSize="sm"
                  borderRadius="lg"
                />
              </FormControl>

              <Alert status="info" fontSize="xs" borderRadius="lg">
                <AlertIcon boxSize={4} />
                This will mark the issue as resolved and update its status.
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" bg={footerBg}>
            <Button variant="ghost" mr={3} onClick={onResolveModalClose} size="md" borderRadius="lg">
              Cancel
            </Button>
            <Button 
              colorScheme="green" 
              onClick={handleSubmitResolution}
              leftIcon={<Icon as={MdCheckCircle} />}
              size="md"
              borderRadius="lg"
              boxShadow="md"
            >
              Mark as Resolved
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default IssueTable;